import { Alert, Card, Col, Progress, Row, Space, Statistic } from "antd";
import {
  AlertOutlined,
  AreaChartOutlined,
  BlockOutlined,
  CheckCircleOutlined,
  DropboxOutlined,
  EnvironmentOutlined,
  ExperimentOutlined,
  WarningOutlined,
} from "@ant-design/icons";

import type { AuditPlot } from "../../../types";

interface Props {
  audit: any;
  auditPlots: AuditPlot[];
  cpCount: number | null;
  acCount: number | null;
  photosCount: number | null;
  onNavigateTab: (key: string) => void;
}

/**
 * Vista "Resumen" del detalle de auditoría. Se renderiza DEBAJO de
 * la ficha de "Información Básica" + mapa + contactos que ya existen
 * en show.tsx — este componente añade dos cosas que faltaban:
 *
 *  1. **KPIs clave** — parcelas aforadas, kg y ha agregados, muestras.
 *  2. **Navegación rápida** — accesos directos a las sub-pestañas de
 *     "Detalle" con el count de cada una.
 *
 * Todos los datos salen del state que mantiene `show.tsx` (`audit`,
 * `auditPlots`, `cpCount`, `acCount`) — cero fetches nuevos.
 */
export const AuditSummaryTab = ({
  audit,
  auditPlots,
  cpCount,
  acCount,
  photosCount,
  onNavigateTab,
}: Props) => {
  if (!audit) return null;

  const totalPlots = auditPlots.length;
  const reviewedPlots = auditPlots.filter((p) => p.reviewed_at).length;
  const reviewPct =
    totalPlots > 0 ? Math.round((reviewedPlots / totalPlots) * 100) : 0;

  const totalKgAuditor = auditPlots.reduce(
    (sum, p) => sum + (parseFloat(p.kg_plot as any) || 0),
    0,
  );
  const declaredKg = Number(audit?.stimated_production) || 0;
  const totalHaAuditor = auditPlots.reduce(
    (sum, p) => sum + (parseFloat(p.surface as any) || 0),
    0,
  );
  const licensedHa = Number(audit?.farm?.surface_tango) || 0;

  const samplesCount = audit?.samples_summary?.length ?? 0;

  const kgFmt = (v: number) =>
    `${Math.round(v).toLocaleString("es-ES")} kg`;
  const haFmt = (v: number) =>
    `${v.toLocaleString("es-ES", { maximumFractionDigits: 3 })} ha`;

  // Resumen de pendientes para finalizar (viene del backend en
  // `audit.pending_summary`). Mostramos un alert amarillo cuando hay
  // algo pendiente — si la audit ya está finalizada el serializer
  // devuelve todos los contadores a 0 y el alert se oculta solo.
  const pending = audit?.pending_summary ?? null;
  const pendingBuckets: {
    key: string;
    label: string;
    tabKey: string;
    bucket?: { count: number; names: string[]; has_more?: boolean };
  }[] = pending
    ? [
        {
          key: "plots",
          label: "parcelas sin revisar",
          tabKey: "parcelas",
          bucket: pending.plots,
        },
        {
          key: "cps",
          label: "puntos de control sin revisar",
          tabKey: "puntos-control",
          bucket: pending.control_points,
        },
        {
          key: "acs",
          label: "cultivos colindantes sin revisar",
          tabKey: "variedades",
          bucket: pending.adjacent_crops,
        },
        {
          key: "samples",
          label: "muestras sin código de laboratorio",
          tabKey: "muestras",
          bucket: pending.samples_without_code,
        },
      ].filter((b) => (b.bucket?.count ?? 0) > 0)
    : [];

  return (
    <div>
      {pendingBuckets.length > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
          message="Pendiente para finalizar"
          description={
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              {pendingBuckets.map((b) => (
                <div key={b.key}>
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigateTab(b.tabKey);
                    }}
                    style={{ fontWeight: 500 }}
                  >
                    {b.bucket!.count} {b.label}
                  </a>
                  {b.bucket!.names.length > 0 && (
                    <span style={{ color: "#8c8c8c", marginLeft: 8 }}>
                      ·{" "}
                      {b.bucket!.names.slice(0, 5).join(", ")}
                      {b.bucket!.names.length > 5 ||
                      b.bucket!.has_more
                        ? "…"
                        : ""}
                    </span>
                  )}
                </div>
              ))}
            </Space>
          }
        />
      )}

      {/* ── KPIs ──────────────────────────────────────────────── */}
      {/* 4 cards. Las dos "comparativas" (Aforo total, Superficie
          auditada) llevan dos Statistics side-by-side dentro del
          mismo Card: una columna con el valor del auditor y la
          otra con el dato de referencia (declarado productor o
          licenciada contractual). Mantiene las 4 cards y da
          visibilidad real a los dos datos. */}
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card style={{ height: "100%" }}>
            <Statistic
              title="Parcelas aforadas"
              value={reviewedPlots}
              suffix={`/ ${totalPlots}`}
              prefix={<BlockOutlined />}
            />
            <Progress
              percent={reviewPct}
              size="small"
              strokeColor="#52c41a"
              showInfo={false}
              style={{ marginTop: 6 }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card
            style={{ height: "100%" }}
            title={
              <Space size={6}>
                <DropboxOutlined />
                <span>Aforo total</span>
              </Space>
            }
            size="small"
          >
            {/* Dos filas dentro del card: Auditor arriba, Declarado
                productor debajo. Cada fila con label + valor en la
                misma línea (nowrap). Tamaño del valor cercano al
                default de `Statistic` (24px) para que la card no se
                vea "enana" al lado de "Parcelas aforadas" y
                "Muestras". */}
            <Space direction="vertical" size={2} style={{ width: "100%" }}>
              <div style={{ whiteSpace: "nowrap" }}>
                <span style={{ fontSize: 13, color: "#8c8c8c", marginRight: 8 }}>
                  Auditor:
                </span>
                <span style={{ fontSize: 22, color: "#52c41a", fontWeight: 500 }}>
                  {kgFmt(totalKgAuditor)}
                </span>
              </div>
              <div style={{ whiteSpace: "nowrap" }}>
                <span style={{ fontSize: 13, color: "#8c8c8c", marginRight: 8 }}>
                  Declarado:
                </span>
                <span style={{ fontSize: 22, color: "#1890ff", fontWeight: 500 }}>
                  {kgFmt(declaredKg)}
                </span>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card
            style={{ height: "100%" }}
            title={
              <Space size={6}>
                <AreaChartOutlined />
                <span>Superficie</span>
              </Space>
            }
            size="small"
          >
            <Space direction="vertical" size={2} style={{ width: "100%" }}>
              <div style={{ whiteSpace: "nowrap" }}>
                <span style={{ fontSize: 13, color: "#8c8c8c", marginRight: 8 }}>
                  Auditada:
                </span>
                <span style={{ fontSize: 22, color: "#1890ff", fontWeight: 500 }}>
                  {haFmt(totalHaAuditor)}
                </span>
              </div>
              <div style={{ whiteSpace: "nowrap" }}>
                <span style={{ fontSize: 13, color: "#8c8c8c", marginRight: 8 }}>
                  Licenciada:
                </span>
                <span style={{ fontSize: 22, color: "#722ed1", fontWeight: 500 }}>
                  {haFmt(licensedHa)}
                </span>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card style={{ height: "100%" }}>
            <Statistic
              title="Muestras"
              value={samplesCount}
              prefix={<ExperimentOutlined />}
              valueStyle={{ color: "#13c2c2" }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Navegación rápida ────────────────────────────────── */}
      <Card
        title="Ir a sección"
        style={{ marginTop: 16 }}
        styles={{ body: { padding: 12 } }}
      >
        <Row gutter={[12, 12]}>
          <Col xs={12} md={6}>
            <Card
              hoverable
              onClick={() => onNavigateTab("parcelas")}
              size="small"
            >
              <Space>
                <BlockOutlined style={{ fontSize: 18 }} />
                <div>
                  <div style={{ fontWeight: 500 }}>Parcelas</div>
                  <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                    {totalPlots} parcela{totalPlots === 1 ? "" : "s"}
                    {totalPlots > 0 && ` · ${reviewPct}% aforado`}
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card
              hoverable
              onClick={() => onNavigateTab("puntos-control")}
              size="small"
            >
              <Space>
                <AlertOutlined style={{ fontSize: 18 }} />
                <div>
                  <div style={{ fontWeight: 500 }}>Puntos de control</div>
                  <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                    {cpCount ?? 0} punto{(cpCount ?? 0) === 1 ? "" : "s"}
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card
              hoverable
              onClick={() => onNavigateTab("variedades")}
              size="small"
            >
              <Space>
                <EnvironmentOutlined style={{ fontSize: 18 }} />
                <div>
                  <div style={{ fontWeight: 500 }}>Colindantes</div>
                  <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                    {acCount ?? 0} colindante
                    {(acCount ?? 0) === 1 ? "" : "s"}
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card
              hoverable
              onClick={() => onNavigateTab("muestras")}
              size="small"
            >
              <Space>
                <ExperimentOutlined style={{ fontSize: 18 }} />
                <div>
                  <div style={{ fontWeight: 500 }}>Muestras</div>
                  <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                    {samplesCount} muestra{samplesCount === 1 ? "" : "s"}
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card
              hoverable
              onClick={() => onNavigateTab("incidencias")}
              size="small"
            >
              <Space>
                <WarningOutlined style={{ fontSize: 18 }} />
                <div>
                  <div style={{ fontWeight: 500 }}>Incidencias</div>
                  <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                    Ir al listado
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card
              hoverable
              onClick={() => onNavigateTab("fotos")}
              size="small"
            >
              <Space>
                <CheckCircleOutlined style={{ fontSize: 18 }} />
                <div>
                  <div style={{ fontWeight: 500 }}>Fotos</div>
                  <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                    {photosCount != null
                      ? `${photosCount} foto${photosCount === 1 ? "" : "s"}`
                      : "Galería de fotos"}
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
};
